import React, { useRef, useLayoutEffect, ReactNode } from "react";
import styles from "./CustomScrollbar.module.css";

type Direction = "vertical" | "horizontal" | "both";

interface Props {
    children: ReactNode;
    direction?: Direction;
    className?: string;
}

const CustomScrollbar: React.FC<Props> = ({ children, direction = "both", className = "" }) => {
    const contentRef = useRef<HTMLDivElement>(null);
    const vThumbRef = useRef<HTMLDivElement>(null);
    const hThumbRef = useRef<HTMLDivElement>(null);
    const rafRef = useRef<number | null>(null);

    const isDraggingRef = useRef<"vertical" | "horizontal" | null>(null);
    const dragStartRef = useRef(0);
    const scrollStartRef = useRef(0);

    const updateThumbs = () => {
        const el = contentRef.current;
        if (!el) return;

        const { scrollTop, scrollLeft, scrollHeight, scrollWidth, clientHeight, clientWidth } = el;

        // Vertical
        if ((direction === "vertical" || direction === "both") && vThumbRef.current) {
            const ratio = clientHeight / scrollHeight;
            if (ratio >= 1) {
                vThumbRef.current.style.display = "none";
            } else {
                const thumbHeight = Math.max(clientHeight * ratio, 30);
                const scrollRatio = scrollTop / (scrollHeight - clientHeight || 1);
                const offset = scrollRatio * (clientHeight - thumbHeight);

                vThumbRef.current.style.height = `${thumbHeight}px`;
                vThumbRef.current.style.transform = `translateY(${offset}px)`;
                vThumbRef.current.style.display = `block`;
            }
        }

        // Horizontal
        if ((direction === "horizontal" || direction === "both") && hThumbRef.current) {
            const ratio = clientWidth / scrollWidth;
            if (ratio >= 1) {
                hThumbRef.current.style.display = "none";
            } else {
                const thumbWidth = Math.max(clientWidth * ratio, 30);
                const scrollRatio = scrollLeft / (scrollWidth - clientWidth || 1);
                const offset = scrollRatio * (clientWidth - thumbWidth);

                hThumbRef.current.style.width = `${thumbWidth}px`;
                hThumbRef.current.style.transform = `translateX(${offset}px)`;
                hThumbRef.current.style.display = `block`;
            }
        }
    };

    const handleScroll = () => {
        if (rafRef.current) cancelAnimationFrame(rafRef.current);
        rafRef.current = requestAnimationFrame(updateThumbs);
    };

    const onMouseDown = (e: React.MouseEvent, axis: "vertical" | "horizontal") => {
        e.preventDefault();

        const el = contentRef.current;
        if (!el) return;

        isDraggingRef.current = axis;
        dragStartRef.current = axis === "vertical" ? e.clientY : e.clientX;
        scrollStartRef.current = axis === "vertical" ? el.scrollTop : el.scrollLeft;

        document.addEventListener("mousemove", onMouseMove);
        document.addEventListener("mouseup", onMouseUp);
    };

    const onMouseMove = (e: MouseEvent) => {
        const el = contentRef.current;
        if (!el || !isDraggingRef.current) return;

        const axis = isDraggingRef.current;

        if (axis === "vertical") {
            const delta = e.clientY - dragStartRef.current;
            const { scrollHeight, clientHeight } = el;

            const scrollRatio = scrollHeight / clientHeight;
            el.scrollTop = scrollStartRef.current + delta * scrollRatio;
        }

        if (axis === "horizontal") {
            const delta = e.clientX - dragStartRef.current;
            const { scrollWidth, clientWidth } = el;

            const scrollRatio = scrollWidth / clientWidth;
            el.scrollLeft = scrollStartRef.current + delta * scrollRatio;
        }
    };

    const onTouchStart = (e: React.TouchEvent, axis: "vertical" | "horizontal") => {
        const el = contentRef.current;
        if (!el) return;

        isDraggingRef.current = axis;
        dragStartRef.current = axis === "vertical" ? e.touches[0].clientY : e.touches[0].clientX;

        scrollStartRef.current = axis === "vertical" ? el.scrollTop : el.scrollLeft;

        document.addEventListener("touchmove", onTouchMove, { passive: false });
        document.addEventListener("touchend", onTouchEnd);
    };

    const onTouchMove = (e: TouchEvent) => {
        const el = contentRef.current;
        if (!el || !isDraggingRef.current) return;

        e.preventDefault(); // IMPORTANT

        const axis = isDraggingRef.current;

        if (axis === "vertical") {
            const delta = e.touches[0].clientY - dragStartRef.current;
            const { scrollHeight, clientHeight } = el;
            const scrollRatio = scrollHeight / clientHeight;
            el.scrollTop = scrollStartRef.current + delta * scrollRatio;
        }

        if (axis === "horizontal") {
            const delta = e.touches[0].clientX - dragStartRef.current;
            const { scrollWidth, clientWidth } = el;
            const scrollRatio = scrollWidth / clientWidth;
            el.scrollLeft = scrollStartRef.current + delta * scrollRatio;
        }
    };

    const onTouchEnd = () => {
        isDraggingRef.current = null;
        document.removeEventListener("touchmove", onTouchMove);
        document.removeEventListener("touchend", onTouchEnd);
    };

    const onMouseUp = () => {
        isDraggingRef.current = null;
        document.removeEventListener("mousemove", onMouseMove);
        document.removeEventListener("mouseup", onMouseUp);
    };

    useLayoutEffect(() => {
        const el = contentRef.current;
        if (!el) return;

        updateThumbs();

        el.addEventListener("scroll", handleScroll);

        const resizeObserver = new ResizeObserver(updateThumbs);
        resizeObserver.observe(el);

        // Also observe children to catch content changes that don't resize the container itself
        if (el.firstElementChild) {
            resizeObserver.observe(el.firstElementChild);
        }

        return () => {
            el.removeEventListener("scroll", handleScroll);
            resizeObserver.disconnect();
            if (rafRef.current) cancelAnimationFrame(rafRef.current);
        };
    }, []);

    return (
        <div className={`${styles.wrapper} ${className}`}>
            <div ref={contentRef} className={styles.content}>
                {children}
            </div>

            {(direction === "vertical" || direction === "both") && (
                <div className={`${styles.scrollbar} ${styles.vertical}`}>
                    <div
                        ref={vThumbRef}
                        className={styles.thumb}
                        onMouseDown={(e) => onMouseDown(e, "vertical")}
                        onTouchStart={(e) => onTouchStart(e, "vertical")}
                    />
                </div>
            )}

            {(direction === "horizontal" || direction === "both") && (
                <div className={`${styles.scrollbar} ${styles.horizontal}`}>
                    <div
                        ref={hThumbRef}
                        className={styles.thumb}
                        onMouseDown={(e) => onMouseDown(e, "horizontal")}
                        onTouchStart={(e) => onTouchStart(e, "horizontal")}
                    />
                </div>
            )}
        </div>
    );
};

export default CustomScrollbar;
